import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { i18n } from "i18n/i18n";
import { TenantConfigService } from "../../../types/tenantConfig";
import { SERVICE_ENTRY_ROUTE } from "../serviceEntryRoutes";
import { liveUseCasesWhere, UseCase } from "../../../util/useCases";

/**
 * Default (kolula-provided) explainer banner, shown when a tenant has not
 * created a banner of its own — see `docs/architecture/end-user-banner-types.md`
 * ("Default banners"). It is rendered from i18n text plus the tenant's primary
 * color rather than served as an image, so it needs no upload, no seed row and
 * no per-language asset, and it is correct for every tenant from day one.
 */

/** The use cases a default banner may exist for: every live use case of the
 *  description (a sports hall's included). `voucher` and `moreToCome` are
 *  home-screen entries, not bookable use cases, so they get none. Whether one
 *  has copy is the i18n check below. */
export const DEFAULT_BANNER_SERVICES: readonly UseCase[] = liveUseCasesWhere(() => true);

export type DefaultBannerService = UseCase;

const isDefaultBannerService = (
  service: string
): service is DefaultBannerService =>
  (DEFAULT_BANNER_SERVICES as readonly string[]).includes(service);

/**
 * The default banners to show for a tenant's enabled use cases, in the tenant's
 * own service order. A use case without copy is dropped rather than rendered as
 * an empty frame — the banner slot must never be a blank box again.
 *
 * Only the external (guest / customer) copy exists today; the internal variant
 * is written down in the architecture doc and ships with the audience switch.
 */
export const pickDefaultBannerServices = (
  services: readonly TenantConfigService[] | undefined
): DefaultBannerService[] =>
  Array.isArray(services)
    ? services
        .filter(isDefaultBannerService)
        .filter((service) => i18n.exists(`home.default_banner.${service}.title`))
    : [];

interface Props {
  service: DefaultBannerService;
  onClick?: () => void;
}

const DefaultBanner = ({ service, onClick }: Props) => {
  const { t } = useTranslation();

  return (
    <Link
      to={SERVICE_ENTRY_ROUTE[service]}
      onClick={onClick}
      className="block w-full"
    >
      <div className="relative flex aspect-[16/7] w-full flex-col justify-center overflow-hidden rounded-xl bg-foreground/5 px-4 above-iphone-se:px-5">
        {/* Decorative wave ground. Tinted with the tenant primary (ADR 0021) so
            one template fits every tenant; no accent color is hardcoded. */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full text-primary/10"
          viewBox="0 0 400 175"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M0 108 C 60 78, 120 138, 200 108 S 340 78, 400 103 L400 175 L0 175 Z"
            fill="currentColor"
          />
          <path
            d="M0 140 C 70 110, 130 170, 210 140 S 350 115, 400 135 L400 175 L0 175 Z"
            fill="currentColor"
          />
        </svg>

        <div className="relative">
          {/* The frame is a fixed 16:7, so headline and subline are clamped to
              two lines and the whole block scales down below iPhone-SE width —
              at 320px the full-size type overflows and clips the CTA. */}
          <p className="line-clamp-2 text-sm font-semibold leading-tight text-foreground above-iphone-se:text-lg">
            {t(`home.default_banner.${service}.title`)}
          </p>
          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-foreground/70 above-iphone-se:mt-1 above-iphone-se:text-sm">
            {t(`home.default_banner.${service}.subtitle`)}
          </p>
          {/* Looks like a button, but the whole banner is the link — a tap
              anywhere opens the flow. */}
          <span className="mt-2 inline-block rounded-full bg-primary px-3 py-1 text-xs font-medium text-onPrimary above-iphone-se:mt-3 above-iphone-se:px-4 above-iphone-se:py-1.5">
            {t(`home.default_banner.${service}.cta`)}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default DefaultBanner;
